import { useBusinessEntity, useSupportedCorporations } from '@commenda/api';
import {
  OwnershipNouns,
  OwnershipVocabularyEntry,
} from '@commenda/types/src/corporation';

export type { OwnershipNouns } from '@commenda/types/src/corporation';

export interface OwnershipVocabulary extends OwnershipNouns {
  // Whether amounts are tracked as units. From the backend — do not work it out
  // from a holding type here, or every screen ends up carrying the taxonomy.
  tracksUnits: boolean;
  supportsMultipleClasses: boolean;
  // Null for a partnership — nothing that would print this should render.
  classNouns: OwnershipNouns | null;
  // What to label a class field with when one still has to be shown — a partnership
  // that isn't in the one-class state gets a picker, and it mustn't say "share class".
  classFieldNouns: OwnershipNouns;
  // What the amount held in a class is called, in the incorporation node's words.
  totalLabel: string;
  // What to call the screen that edits those amounts.
  sectionTitle: string;
}

const GENERIC_CLASS_NOUNS: OwnershipNouns = {
  singular: 'Class',
  plural: 'Classes',
  lowerSingular: 'class',
  lowerPlural: 'classes',
};

// Shown while the entity and content queries load, and if a deployment ever
// serves an entry the backend has no vocabulary for. Shareholder copy because
// that is what every screen said before any of this was content-driven.
const FALLBACK: OwnershipVocabularyEntry = {
  holder: {
    singular: 'Shareholder',
    plural: 'Shareholders',
    lowerSingular: 'shareholder',
    lowerPlural: 'shareholders',
  },
  klass: {
    singular: 'Share class',
    plural: 'Share classes',
    lowerSingular: 'share class',
    lowerPlural: 'share classes',
  },
  totalLabel: 'Total shares',
  sectionTitle: 'Share classes',
};

// An entity that hasn't named a class is describing capital, not units. The
// tracksUnits half is the backend's answer; the class count is live data.
export const isCapitalOnlyLlc = (
  tracksUnits: boolean,
  stockClasses: { hasIssuedUnits: boolean | null }[] | undefined
): boolean =>
  tracksUnits &&
  stockClasses?.length === 1 &&
  stockClasses[0].hasIssuedUnits !== true;

export function ownershipVocabulary(
  entry: OwnershipVocabularyEntry | undefined,
  capabilities:
    | { tracksUnits?: boolean; supportsMultipleClasses?: boolean }
    | undefined
): OwnershipVocabulary {
  const words = entry ?? FALLBACK;
  return {
    ...words.holder,
    tracksUnits: capabilities?.tracksUnits ?? false,
    supportsMultipleClasses: capabilities?.supportsMultipleClasses ?? true,
    classNouns: words.klass,
    classFieldNouns: words.klass ?? GENERIC_CLASS_NOUNS,
    totalLabel: words.totalLabel,
    sectionTitle: words.sectionTitle,
  };
}

export function useEntityOwnershipNouns(
  corporationId: number
): OwnershipVocabulary {
  const { data: businessEntity } = useBusinessEntity(corporationId);
  const { data: supported } = useSupportedCorporations();
  const ownership = businessEntity?.generalCorporationInfo.ownership;
  const holdingType = ownership?.holdingType;
  return ownershipVocabulary(
    holdingType ? supported?.ownershipVocabulary?.[holdingType] : undefined,
    ownership
  );
}
